import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useFeature } from '@/features/feature-flags/useFeature';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { useSensitivityMutations } from '@/features/patient-record/hooks/useSensitivityMutations';
import { patientQueries } from '@/features/patient-record/queries';
import { formatDate } from '@/lib/format';

interface SensitiveFlagBlockProps {
  readonly guid: string;
}

/**
 * Sensitive-record toggle (PTV2-1211 / S4 FE).
 *
 * Renders nothing when `breakGlass.enabled` is off for the tenant — the
 * whole feature is gated. When enabled, shows the current state with a
 * flip-on inline reason prompt. Clearing goes through DELETE with no
 * confirmation because the audit trail already preserves the prior state
 * on the server.
 */
export function SensitiveFlagBlock({ guid }: SensitiveFlagBlockProps): ReactElement | null {
  const { t } = useTranslation();
  const breakGlassEnabled = useFeature('breakGlass.enabled') === true;
  const patientQuery = useQuery(patientQueries.detail(guid));
  const { set, clear } = useSensitivityMutations(guid);
  const [reasonDraft, setReasonDraft] = useState('');
  const [prompting, setPrompting] = useState(false);

  // PTV2-1308: resolve the flagging user's GUID to a display name for the
  // banner. Called unconditionally (before the early returns); passing [] while
  // the patient query is still loading runs zero lookups.
  const setByGuid = patientQuery.data?.sensitivity.setBy ?? null;
  const flaggedByNames = useResolveUserDisplayNames(setByGuid !== null ? [setByGuid] : []);
  // Lookup in flight → null (the "set by" part is left out); lookup failed → "Unknown user".
  const flaggedByLabel =
    setByGuid !== null
      ? resolvedNameLabel(flaggedByNames.get(setByGuid), t('patient.careTeam.unknownUser'))
      : null;

  if (!breakGlassEnabled) return null;
  if (!patientQuery.isSuccess) return null;

  const sensitivity = patientQuery.data.sensitivity;
  const pending = set.isPending || clear.isPending;

  function startPrompt(): void {
    setReasonDraft('');
    setPrompting(true);
  }
  function cancelPrompt(): void {
    setPrompting(false);
    setReasonDraft('');
  }
  function submitReason(): void {
    const reason = reasonDraft.trim();
    if (reason.length === 0) return;
    set.mutate(reason, {
      onSuccess: () => {
        setPrompting(false);
        setReasonDraft('');
      },
    });
  }

  if (sensitivity.flagged) {
    return (
      <div
        role="group"
        aria-label={t('patient.consent.sensitive.regionAria')}
        className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1.5"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold text-destructive">
            {t('patient.consent.sensitive.flaggedTitle')}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto h-7"
            onClick={() => {
              clear.mutate(undefined);
            }}
            disabled={pending}
          >
            {t('patient.consent.sensitive.clear')}
          </Button>
        </div>
        {sensitivity.reason !== null && (
          <p className="text-sm text-foreground/90">
            <span className="text-muted-foreground">
              {t('patient.consent.sensitive.reasonLabel')}
              {': '}
            </span>
            {sensitivity.reason}
          </p>
        )}
        {(flaggedByLabel !== null || sensitivity.setAt !== null) && (
          <p className="text-xs text-muted-foreground">
            {flaggedByLabel !== null &&
              t('patient.consent.sensitive.setBy', { value: flaggedByLabel })}
            {flaggedByLabel !== null && sensitivity.setAt !== null && ' · '}
            {sensitivity.setAt !== null &&
              t('patient.consent.sensitive.setAt', {
                value: formatDate(sensitivity.setAt),
              })}
          </p>
        )}
      </div>
    );
  }

  if (prompting) {
    return (
      <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-3 space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium">
            {t('patient.consent.sensitive.promptTitle')}
          </span>
        </div>
        <label className="block space-y-1 text-xs font-medium">
          {t('patient.consent.sensitive.reasonLabel')}
          <Textarea
            value={reasonDraft}
            rows={2}
            maxLength={500}
            placeholder={t('patient.consent.sensitive.reasonPlaceholder')}
            onChange={(event) => {
              setReasonDraft(event.target.value);
            }}
          />
        </label>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={cancelPrompt} disabled={pending}>
            {t('patient.consent.sensitive.cancel')}
          </Button>
          <Button
            type="button"
            onClick={submitReason}
            disabled={reasonDraft.trim().length === 0 || pending}
            loading={pending}
          >
            {t('patient.consent.sensitive.submit')}
          </Button>
        </div>
      </div>
    );
  }

  // Idle: subtle prompt to flip the flag on.
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-2">
      <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="text-xs text-muted-foreground flex-1 min-w-0">
        {t('patient.consent.sensitive.idleHint')}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7"
        onClick={startPrompt}
        disabled={pending}
      >
        {t('patient.consent.sensitive.flag')}
      </Button>
    </div>
  );
}
